import React, { useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import { TableBody } from './TableBody';
import { TableHeader } from './TableHeader';
import { TableFooter } from './TableFooter';
import { User } from '../../../../../../entities/User';
import { HrisEmployee } from '../../../../../../entities/applicant_tracking/HrisEmployees';
import { HrisProvider } from '../../../../../../enums/IntegrationProvider/HrisProvider';
import { AlertObject, AlertType } from '../../../../../../components/Alert';
import { EmailAccountIntegration } from '../../../../../../entities/EmailAccountIntegration';
import { ApplicationListingFilters } from '../ListApplicationsFilters/utils';
import { SortingColumn } from '../../../../../../utils/sorting';
import { NewListCandidate } from '../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { useFeatureFlags } from '../../../../../../contexts/FeatureFlagContext';
import { EditablePushToHrisModal } from '../../../../../CandidatePage/CandidateActions/EditablePushToHrisModal';
import { PushToHrisModal } from '../../../../../CandidatePage/CandidateActions/PushToHrisModal';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner';
import { RejectCandidateModal } from '../../../../../CandidatePage/CandidateActions/DropdownActions/RejectCandidateModal';
import { SendEmailModal } from '../../../../../SendEmailModal';
import { SendEmailDto } from '../../../../../../services/dtos/EmailDto';
import { ApiError } from '../../../../../../services/ApiService/errors/ApiError';
import { ApplicationService } from '../../../../../../services/applicant_tracking/ApplicationService';

interface PropTypes {
  atsSignableOfferEnabled: boolean;
  candidates: NewListCandidate[];
  currentUser: User;
  emailAccountIntegration: EmailAccountIntegration;
  error: boolean;
  filterStates: ApplicationListingFilters;
  hrisEmployees?: HrisEmployee[];
  hrisProvider?: HrisProvider;
  selectedCandidates: NewListCandidate[];
  totalFilteredCandidates: number;
  offerRequisitionFlowEnabled: boolean;
  bulkSelectedIds: number[] | null;
  bulkSelectionLoading: boolean;
  onSelectAll: () => void;
  onClearSelectAll: () => void;
  onBulkSelectionRemove: (ids: number[]) => void;
  onBulkSelectionAdd: (ids: number[]) => void;
  isLoading: boolean;
  onFilterStatesUpdate: (fs: ApplicationListingFilters) => void;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
  setSelectedCandidates: (candidates: NewListCandidate[]) => void;
}

function filterSelectableCandidates(candidates: NewListCandidate[]) {
  return candidates
    .filter((candidate) => !candidate.anonymized)
    .filter((candidate) => candidate.status !== 'draft')
    .filter((candidate) => candidate.application?.bulkActionsPermitted);
}

function isEntireListSelected(
  all: NewListCandidate[],
  selected: NewListCandidate[],
) {
  const selectableList = filterSelectableCandidates(all);
  if (selectableList.length === 0) return false;

  return selectableList.every((c) =>
    selected.map((selectedCandidate) => selectedCandidate.id).includes(c.id),
  );
}

function selectedCountText(selectedCount: number, totalCount?: number) {
  const countLabel =
    totalCount != null && selectedCount < totalCount
      ? `${selectedCount} of ${totalCount}`
      : `${selectedCount}`;
  return selectedCount === 1
    ? `${countLabel} application is selected`
    : `${countLabel} applications are selected`;
}

export function ListApplicationsTable(props: PropTypes) {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);

  const handleRejectModalClose = (sendEmail: boolean) => {
    setIsRejectModalOpen(false);

    if (sendEmail) {
      setIsEmailModalOpen(true);
    }
  };

  const onSendEmail = async (sendEmailProps: SendEmailDto) => {
    await ApplicationService.sendEmails(
      [candidateForAction?.application?.id],
      sendEmailProps,
    )
      .then(() => {
        props.setAlert({
          message: 'This email was successfully sent.',
          type: AlertType.Success,
        });

        props.reloadPage();
      })
      .catch((e: ApiError) => {
        props.setAlert({
          message: e.body?.message || 'The email was not sent.',
          type: AlertType.Danger,
        });
      })
      .finally(() => setIsEmailModalOpen(false));
  };

  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const {
    atsEditBeforeExportToHrisEnabled,
    atsBulkSelectAllApplicationsEnabled,
  } = useFeatureFlags();
  const [candidateForAction, setCandidateForAction] =
    useState<NewListCandidate>(null);
  const [pushToHrisModalActive, setPushToHrisModalActive] =
    useState<boolean>(false);
  const handlePushToHrisModalClose = () => {
    setPushToHrisModalActive(false);
    props.reloadPage();
  };

  useEffect(() => {
    if (props.bulkSelectedIds !== null) {
      const selectable = filterSelectableCandidates(props.candidates);
      setIsHeaderCheckboxChecked(
        selectable.length > 0 &&
          selectable.every(
            (c) =>
              c.application?.id != null &&
              props.bulkSelectedIds.includes(c.application.id),
          ),
      );
    } else {
      setIsHeaderCheckboxChecked(
        isEntireListSelected(props.candidates, props.selectedCandidates),
      );
    }
  }, [props.candidates, props.selectedCandidates, props.bulkSelectedIds]);

  if (props.isLoading) {
    return <LoadingSpinner addContainer />;
  }

  const selectedCandidates = (checked: boolean) => {
    if (checked) {
      return props.selectedCandidates
        .concat(filterSelectableCandidates(props.candidates))
        .filter((item, index, self) => self.indexOf(item) === index);
    } else {
      return props.selectedCandidates.filter(
        (selectedCandidate) =>
          props.candidates.find(
            (c) =>
              c.id === selectedCandidate.id &&
              c.application?.id === selectedCandidate.application?.id,
          ) == null,
      );
    }
  };

  return (
    <>
      <Table className='mb-0 table-fixed'>
        <TableHeader
          checked={isHeaderCheckboxChecked}
          checkboxDisabled={
            filterSelectableCandidates(props.candidates).length === 0
          }
          onSelectedCandidatesUpdate={(checked: boolean) => {
            props.setSelectedCandidates(selectedCandidates(checked));
            if (props.bulkSelectedIds !== null) {
              const currentPageIds = filterSelectableCandidates(
                props.candidates,
              )
                .map((c) => c.application?.id)
                .filter((id): id is number => id != null);
              if (checked) {
                props.onBulkSelectionAdd(currentPageIds);
              } else {
                props.onBulkSelectionRemove(currentPageIds);
              }
            }
          }}
          sortColumn={props.filterStates.sortingColumn}
          onSortUpdate={(sortingColumn: SortingColumn) =>
            props.onFilterStatesUpdate({
              ...props.filterStates,
              sortingColumn: sortingColumn,
            })
          }
        />
        <TableBody
          atsSignableOfferEnabled={props.atsSignableOfferEnabled}
          candidates={props.candidates}
          currentUser={props.currentUser}
          emailAccountIntegration={props.emailAccountIntegration}
          hrisEmployees={props.hrisEmployees}
          hrisProvider={props.hrisProvider}
          selectedCandidates={props.selectedCandidates}
          bulkSelectedIds={props.bulkSelectedIds}
          onBulkSelectionRemove={props.onBulkSelectionRemove}
          onBulkSelectionAdd={props.onBulkSelectionAdd}
          offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
          setSelectedCandidates={props.setSelectedCandidates}
          reloadPage={props.reloadPage}
          setAlert={props.setAlert}
          candidateForAction={candidateForAction}
          setCandidateForAction={setCandidateForAction}
          setPushToHrisModalActive={setPushToHrisModalActive}
          setIsEmailModalOpen={setIsEmailModalOpen}
          setIsRejectModalOpen={setIsRejectModalOpen}
        />
      </Table>
      <TableFooter
        error={props.error}
        page={props.filterStates.page}
        pageSize={10}
        totalRows={props.totalFilteredCandidates}
        disabled={false}
        additionalInfo={
          (props.selectedCandidates.length > 0 ||
            props.bulkSelectedIds !== null) && (
            <span className='fw-semibold ms-4 text-dark-500'>
              {props.bulkSelectedIds !== null ? (
                <>
                  {selectedCountText(
                    props.bulkSelectedIds.length,
                    props.totalFilteredCandidates,
                  )}{' '}
                  <span
                    className='m-4 text-blue cursor-pointer'
                    role='button'
                    onClick={props.onClearSelectAll}
                  >
                    Clear selection
                  </span>
                </>
              ) : (
                <>
                  {selectedCountText(props.selectedCandidates.length)}
                  {atsBulkSelectAllApplicationsEnabled &&
                    props.totalFilteredCandidates >
                      props.selectedCandidates.length && (
                      <>
                        <span
                          className={`m-4 ${
                            props.bulkSelectionLoading
                              ? 'text-gray-300 disabled pe-none'
                              : 'text-blue cursor-pointer'
                          }`}
                          role='button'
                          onClick={
                            !props.bulkSelectionLoading
                              ? props.onSelectAll
                              : undefined
                          }
                        >
                          {`Select all ${props.totalFilteredCandidates} applications`}
                        </span>
                        {props.bulkSelectionLoading && (
                          <>
                            {' '}
                            <span className='spinner-border spinner-border-sm' />
                          </>
                        )}
                      </>
                    )}
                </>
              )}
            </span>
          )
        }
        onPageChanged={(newPage: number) =>
          props.onFilterStatesUpdate({
            ...props.filterStates,
            page: newPage,
          })
        }
      />
      {atsEditBeforeExportToHrisEnabled ? (
        <EditablePushToHrisModal
          applicationIds={[candidateForAction?.application?.id]}
          applicationId={candidateForAction?.application?.id}
          allowedHrisProvider={props.hrisProvider}
          isOpen={pushToHrisModalActive}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
        />
      ) : (
        <PushToHrisModal
          applicationIds={[candidateForAction?.application?.id]}
          allowedHrisProvider={props.hrisProvider}
          isOpen={pushToHrisModalActive}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
          candidateName={candidateForAction?.displayName}
          jobName={candidateForAction?.application?.job?.name}
        />
      )}
      <SendEmailModal
        applicationIds={[candidateForAction?.application?.id]}
        toEmail={candidateForAction?.email}
        hasEmailConnected={props.currentUser.hasEmailConnected}
        isOpen={isEmailModalOpen}
        onCloseModal={() => setIsEmailModalOpen(false)}
        onSendEmail={onSendEmail}
      />
      <RejectCandidateModal
        applicationId={candidateForAction?.application?.id}
        candidateName={candidateForAction?.displayName}
        jobName={candidateForAction?.application?.job?.name}
        isOpen={isRejectModalOpen}
        onClose={handleRejectModalClose}
        sendEmailEnabled={props.emailAccountIntegration.enabled}
        user={props.currentUser}
        reloadApplication={props.reloadPage}
      />
    </>
  );
}
